import { useRef, useState } from 'react';
import { CheckCircle2, FileText, Upload } from 'lucide-react';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { useImportContacts } from '@/hooks/use-contacts';
import { describeError } from '@/lib/describe-error';
import type { ContactImportResult } from '@/lib/schemas';

/**
 * The CSV import.
 *
 * The consent fields are required and NOT hidden behind an "advanced" disclosure, deliberately: the
 * server refuses an import without them, and burying the one field that cannot be reconstructed later
 * is how it ends up filled with "n/a".
 */
export function ImportContactsDialog({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [consentSource, setConsentSource] = useState('');
  const [consentWording, setConsentWording] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const fileInput = useRef<HTMLInputElement>(null);
  const importContacts = useImportContacts();

  const errors = validate({ file, consentSource, consentWording });
  const result = importContacts.data;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    // Everything is marked touched on submit so errors appear beside their fields, and the first bad
    // one is focused. The button is NOT disabled for invalidity — a disabled button gives no reason
    // and no keyboard affordance (ui-ux.md § 6).
    setTouched({ file: true, consentSource: true, consentWording: true });

    const firstBad = Object.keys(errors)[0];

    // `file === null` is already covered by `errors.file`, but naming it here is what lets the
    // compiler see that `file` is a File on the line below.
    if (firstBad !== undefined || file === null) {
      document.getElementById(firstBad ?? 'file')?.focus();
      return;
    }

    importContacts.mutate({ file, consentSource, consentWording });
  }

  if (result !== undefined) {
    return (
      <Modal
        title="Import finished"
        onClose={onClose}
        footer={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <ImportSummary result={result} />
      </Modal>
    );
  }

  return (
    <Modal
      title="Import contacts from CSV"
      description="The file needs an 'email' column. Any other column becomes a custom field you can segment on."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={importContacts.isPending}>
            Cancel
          </Button>

          <Button
            type="submit"
            form="import-form"
            variant="primary"
            // Disabled DURING submission, which is different from disabled for invalidity.
            disabled={importContacts.isPending}
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            {importContacts.isPending ? 'Importing…' : 'Import'}
          </Button>
        </>
      }
    >
      <form id="import-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Field
          id="file"
          label="CSV file"
          error={touched.file === true ? errors.file : undefined}
          hint="Up to 50,000 rows. Nothing is stored — the file is read and discarded."
        >
          <input
            id="file"
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            onBlur={() => setTouched((previous) => ({ ...previous, file: true }))}
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            aria-invalid={touched.file === true && errors.file !== undefined}
            aria-describedby={errors.file === undefined ? 'file-hint' : 'file-error'}
            className="block w-full rounded-md border border-border bg-surface text-sm text-foreground file:mr-3 file:border-0 file:bg-surface-2 file:px-3 file:py-2 file:text-sm file:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>

        <Field
          id="consentSource"
          label="Where did the consent come from?"
          error={touched.consentSource === true ? errors.consentSource : undefined}
          hint="A form name, an event, a contract — whatever identifies how these people opted in."
        >
          <input
            id="consentSource"
            type="text"
            value={consentSource}
            onChange={(event) => setConsentSource(event.target.value)}
            onBlur={() => setTouched((previous) => ({ ...previous, consentSource: true }))}
            aria-invalid={touched.consentSource === true && errors.consentSource !== undefined}
            aria-describedby={
              errors.consentSource === undefined ? 'consentSource-hint' : 'consentSource-error'
            }
            placeholder="Website signup form"
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>

        <Field
          id="consentWording"
          label="What exactly were they shown?"
          error={touched.consentWording === true ? errors.consentWording : undefined}
          hint="The actual text, not a description of it. This is what would be produced in a complaint."
        >
          <textarea
            id="consentWording"
            value={consentWording}
            onChange={(event) => setConsentWording(event.target.value)}
            onBlur={() => setTouched((previous) => ({ ...previous, consentWording: true }))}
            rows={3}
            aria-invalid={touched.consentWording === true && errors.consentWording !== undefined}
            aria-describedby={
              errors.consentWording === undefined ? 'consentWording-hint' : 'consentWording-error'
            }
            placeholder="Yes, email me about Hazcheck products and services."
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>

        {importContacts.isError && (
          // Announced, because somebody who tabbed away while it uploaded never sees it otherwise.
          <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
            {describeError(importContacts.error).message}
          </p>
        )}
      </form>
    </Modal>
  );
}

/** Counts plus the capped row-level failures, which is what makes a bad file fixable. */
function ImportSummary({ result }: { result: ContactImportResult }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-ink" aria-hidden="true" />

        <p className="text-sm text-foreground">
          Read {result.totalRows.toLocaleString()} rows: created{' '}
          <strong className="text-heading">{result.created.toLocaleString()}</strong>, updated{' '}
          <strong className="text-heading">{result.updated.toLocaleString()}</strong>.
        </p>
      </div>

      {(result.skipped > 0 || result.invalid > 0) && (
        <p className="rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-ink">
          {result.skipped.toLocaleString()} skipped and {result.invalid.toLocaleString()} invalid.
        </p>
      )}

      {result.errors.length > 0 && (
        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium text-heading">
            <FileText className="h-4 w-4 text-muted" aria-hidden="true" />
            Rows that need attention
          </h3>

          {/* Row NUMBERS, not addresses — the server deliberately does not return the addresses, and
              a list of failed email addresses on screen would be a PII export by another name. */}
          <ul className="mt-2 max-h-48 overflow-y-auto rounded-md border border-border bg-surface text-sm">
            {result.errors.map((error) => (
              <li
                key={error.rowNumber}
                className="flex gap-3 border-b border-border px-3 py-1.5 last:border-b-0"
              >
                <span className="w-14 shrink-0 tabular-nums text-muted">Row {error.rowNumber}</span>
                <span className="text-foreground">{error.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Label, hint and error wired to the control with aria-describedby. */

/**
 * The same rules the server enforces. Duplicated deliberately so the author gets an answer without a
 * round trip — the server remains the authority and will refuse the same things.
 */
function validate(input: {
  file: File | null;
  consentSource: string;
  consentWording: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};

  if (input.file === null) {
    errors.file = 'Choose a CSV file.';
  } else if (!/\.(csv|txt)$/i.test(input.file.name)) {
    errors.file = 'That is not a .csv file. Export one from your spreadsheet if you have an .xlsx.';
  }

  if (input.consentSource.trim() === '') {
    errors.consentSource = 'Record where the consent came from.';
  }

  if (input.consentWording.trim() === '') {
    errors.consentWording = 'Record the wording these people were shown.';
  }

  return errors;
}
